import { useEffect, useMemo, useState } from "react";
import useParks from "../../../hooks/api-calls/useParks";
import useWindTurbine from "../../../hooks/api-calls/useWindTurbine";
import useUsers from "../../../hooks/api-calls/useUsers";
import useSpecies from "../../../hooks/api-calls/useSpecies";
import CustomSelect from "../../../components/forms/CustomSelect";
import AddSpecieModal from "./AddSpecieModal";
import { AuthState } from "../../../redux/authSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";

interface StopsEntitySelectorsProps {
    formik: any;
    isFromPark?: boolean;
    setSpeciesWithQuantities: any;
    speciesWithQuantities: any;
}
const StopsEntitySelectors: React.FC<StopsEntitySelectorsProps> = ({ formik, isFromPark , setSpeciesWithQuantities, speciesWithQuantities }) => {

    // STATES 

    const [selectedSpecie, setSelectedSpecie] = useState(null);
    const [quantityModal, setQuantityModal] = useState(false);
    const { user }: AuthState = useSelector((state: RootState) => state.auth);

    const isAdmin = user?.roles?.includes('Administrador') || user?.roles?.includes('Superadministrador') ? true : false;

    // HOOKS

    const { getParksList } = useParks();
    const { getWindTurbinesList , updateWindTurbineHookFilters } = useWindTurbine();
    const { getUsersList } = useUsers();
    const { getSpeciesListScientific } = useSpecies({ censusAvailable: true });

    const censusSpeciesOptions = useMemo(() => {
        const list = getSpeciesListScientific();
        return list
            .filter((specie: any) => !speciesWithQuantities.some(
                (s: any) => (s.id || s.value) === specie.value
            ))
            .sort((a: any, b: any) =>
                (a.scientificName ?? a.label ?? '').localeCompare(
                    b.scientificName ?? b.label ?? '',
                    'es',
                    { sensitivity: 'base' }
                )
            );
    }, [speciesWithQuantities, getSpeciesListScientific().length]);

    // USE EFFECTS

    useEffect(() => {
        updateWindTurbineHookFilters({
            filter_filters: {
                park: formik.values.parkId,
            },
        });
    }, [formik.values.parkId]);

    useEffect(() => {
        formik.setFieldValue(
            'species', 
            speciesWithQuantities.map((s : any) => ({
                id: s.value || s.id,
                numIndividuals: s.numIndividuals,
                commonName: s.commonName,
            }))
        );
    }, [speciesWithQuantities]);

    useEffect(() => {
        if (!isAdmin && user?.id) {
            formik.setFieldValue('userId', user.id);
        }
    }, [isAdmin, user?.id]);

    // RENDER

    return (
        <>

            <div className="grid md:grid-cols-2 grid-cols-1 gap-2">

                {/* PARK SELECTOR */}

                <CustomSelect
                    isSearchable
                    id={'park'}
                    label="Parque Eólico"
                    isDisabled={isFromPark}
                    options={getParksList()}
                    onChange={(e: any) => { 
                        formik.setFieldValue('parkId', e.value)
                    }}
                    value={
                        getParksList().filter((park: any) => park.value === formik.values.parkId)
                    }
                    required
                    error={formik.errors.parkId && formik.touched.parkId ? formik.errors.parkId : undefined}
                />

                {/* TURBINE SELECTOR */}

                <CustomSelect
                    isMulti
                    isSearchable
                    id={'windTurbines'}
                    label="Aero(s)"
                    options={getWindTurbinesList()}
                    onChange={(e: any) => {
                        formik.setFieldValue('windTurbines', e.map((turbine: any) => turbine.value))
                        formik.setFieldValue('numMachines', e.length)
                    }}
                    value={
                        getWindTurbinesList().filter((turbine: any) => formik.values.windTurbines.includes(turbine.value))
                    }
                    required
                    error={formik.errors.windTurbines && formik.touched.windTurbines ? formik.errors.windTurbines : undefined}
                />

                {/* USER SELECTOR */}    

                 <CustomSelect
                    isSearchable
                    id={'userId'}
                    label="Tecnico"
                    options={getUsersList()}
                    onChange={(e: any) => {
                        formik.setFieldValue('userId', e.value)
                    }}
                    value={
                        getUsersList().filter((user: any) => user.value === formik.values.userId)
                    }
                    required
                    isDisabled={!isAdmin}
                    error={formik.errors.userId && formik.touched.userId ? formik.errors.userId : undefined}
                />

                <CustomSelect
                    isMulti
                    isSearchable
                    id="species"
                    italic
                    label="Especie(s)"
                    options={censusSpeciesOptions}
                    key={speciesWithQuantities.map((s: any) => s.value).join(',')}
                    onChange={(selectedOptions : any) => {
                        // Manejar selección/deselección
                        selectedOptions.forEach((option: any) => {
                            if (!speciesWithQuantities.some((s: any) => s.value === option.value)) {
                                setSelectedSpecie(option);
                                setQuantityModal(true);
                            }
                        });
                        
                        const removed = speciesWithQuantities.filter(
                            (s: any) => !selectedOptions.some((o: any) => o.value === s.value)
                        );
                        
                        setSpeciesWithQuantities((prev: any) => prev.filter(
                            (s: any) => selectedOptions.some((o: any) => o.value === s.value)
                        ));
                    }}
                    value={speciesWithQuantities}
                    required
                    error={formik.errors.species && formik.touched.species ? formik.errors.species : undefined}
                />

            </div>

            <AddSpecieModal isOpen={quantityModal} setIsOpen={setQuantityModal} setSpeciesWithQuantities={setSpeciesWithQuantities} selectedSpecie={selectedSpecie} />

        </>

    )
}

export default StopsEntitySelectors;